import type React from 'react';
import { styled, css, useTheme } from 'styled-components';

import Icons from '../../Elements/Icons/index.tsx';

export interface IAlert {
  type: 'error' | 'warning' | 'info' | 'success';
  title?: string | React.ReactNode;
  message?: string | React.ReactNode;
  onClose?: () => void;
  reFetch?: () => void;
  spacer?: 'top' | 'bottom';
  spacerSize?: number;
}

export const ActionGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ActionButton = styled.button`
  cursor: pointer;
  width: 24px;
  height: 24px;
  line-height: 0;
  background: none;
  border: none;
  outline: none;
  box-shadow: none;
  margin: auto;
  padding: 3px;
`;

const Message = styled.div`
  ${({ theme }) => theme.typography.textMedium}
`;

const Title = styled.div`
  ${({ theme }) => theme.typography.textLarge}
  ${({ theme }) => theme.typography.weightMedium}
`;

const Body = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
`;

const Icon = styled.div`
  position: absolute;
  top: 14px;
  left: 24px;
  width: 24px;
  height: 24px;
  line-height: 0;
`;

interface IWrapper {
  $styled: Pick<IAlert, 'type' | 'spacer' | 'spacerSize'>;
}

const Wrapper = styled.div<IWrapper>`
  position: relative;
  min-height: 54px;
  width: 100%;
  padding: 15px 15px 15px 64px;
  color: rgb(0 0 0 / 65%);
  border-radius: 3px;
  ${({ $styled: { type }, theme }) =>
    type === 'success' &&
    css`
      background-color: ${theme.colors.positive10};
      border: 1px solid ${theme.colors.positive30};
      color: ${theme.colors.positive120};
    `}
  ${({ $styled: { type }, theme }) =>
    type === 'info' &&
    css`
      background-color: ${theme.colors.link10};
      border: 1px solid ${theme.colors.link30};
      color: ${theme.colors.link120};
    `}
  ${({ $styled: { type }, theme }) =>
    type === 'warning' &&
    css`
      background-color: ${theme.colors.warning10};
      border: 1px solid ${theme.colors.warning30};
      color: ${theme.colors.warning120};
    `}
  ${({ $styled: { type }, theme }) =>
    type === 'error' &&
    css`
      background-color: ${theme.colors.critical10};
      border: 1px solid ${theme.colors.critical30};
      color: ${theme.colors.critical120};
    `}
  ${({ $styled: { spacer, spacerSize } }) =>
    spacer === 'top' &&
    css`
      margin-top: ${spacerSize ? `${spacerSize}px` : '15px'};
    `}
  ${({ $styled: { spacer, spacerSize } }) =>
    spacer === 'bottom' &&
    css`
      margin-bottom: ${spacerSize ? `${spacerSize}px` : '15px'};
    `}
`;

const Alert: React.FC<IAlert> = ({ reFetch, type, title, message, onClose, spacer, spacerSize, ...other }) => {
  const { colors } = useTheme();

  return (
    <Wrapper
      $styled={{
        type,
        spacer,
        spacerSize,
      }}
      {...other}>
      <Icon>
        {type === 'success' && <Icons.Success color={colors.positive120} />}
        {type === 'info' && <Icons.Info color={colors.link120} />}
        {type === 'warning' && <Icons.Warning color={colors.warning120} />}
        {type === 'error' && <Icons.Error color={colors.critical120} />}
      </Icon>
      <Body>
        <div>
          {title && <Title>{title}</Title>}
          {message && <Message>{message}</Message>}
        </div>
        {(reFetch || onClose) && (
          <ActionGroup>
            {reFetch && (
              <ActionButton onClick={reFetch} type='button'>
                <Icons.Reload size={14} />
              </ActionButton>
            )}
            {onClose && (
              <ActionButton onClick={onClose} type='button'>
                <Icons.Close size={14} color={colors.black_3} />
              </ActionButton>
            )}
          </ActionGroup>
        )}
      </Body>
    </Wrapper>
  );
};

export default Alert;
